import React, { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';

import CacheBuster from 'react-cache-buster';
import { ThemeProvider } from 'styled-components';

import { CriticalErrorBoundary } from './CriticalErrorBoundary';

import {
  ACCOUNT,
  ADD_CB,
  ADD_FID_CARD,
  ADD_FID_SCAN,
  ADD_PAYMENT,
  BRAND,
  BRAND_DETAIL,
  CART,
  CHECK,
  COUPONS,
  EDENRED,
  FIDLOGIN,
  GIFT,
  HELP,
  HOME,
  INVITATION,
  LOGIN,
  LOGIN_FID_CARD,
  LOGIN_FID_SCAN,
  ORDER_SUCCESS,
  POPUP_PAYMENT,
  PRODUCT,
  PROMO,
  CONVERSION,
  RECOMPENSES,
  REGISTER,
  RESETPASSWORD,
  REWARD,
  SCAN,
  SCAN_CHECK,
  STORE_LIST,
  TICKETS,
  USER_FID_CARD,
  USER_FID_CARD_DETAIL,
  WALLET,
  NUMBER_SCREEN,
  SET_ACCOUNT,
  CONFIG,
  EDENRED_CONFIG,
  UPDATE_USER, FIDCOUPON,
  RECEIPT, STORE_LOCATION, POLICY,
  PHONE_PASSWORD_LOGIN,
  AMOUNT_CHANGE_JACKPOT
} from './constants/routes';

import { useShadowDOMStyling } from './hooks/useShadowDOMStyling';
import { useFeedbackDrawer } from './hooks/useFeedbackDrawer';
import UserFeedbackDrawer from './components/feedback/UserFeedbackDrawer';

import PageLoader from './components/loader/PageLoader';
import RedirectPage from './pages/RedirectPage';
import AccountScreen from './pages/account/AccountScreen';
import LoginScreen from './pages/auth/LoginScreen';
import RegisterScreen from './pages/auth/register/RegisterScreen';
import Scan from './components/Scan';
import NumberScreen from './pages/account/NumberScreen';
import ConfigScreen from './pages/Config/ConfigPage';
import StoreListScreen from './pages/StoreListScreen';
import WelcomeScreen from './pages/WelcomeScreen';
import CartScreen from './pages/CartScreen';
import ScanCheck from './pages/ScanCheck';
import UserFidelityCardDetail from './pages/account/fid/UserFidelityCardDetail';
import UserAddFidCardScreen from './pages/account/fid/add-card/UserAddFidCardScreen';
import UserAddFidScanScreen from './pages/account/fid/add-card/UserAddFidScanScreen';
import SetAccountScreen from './pages/account/setAccout/SetAccountScreen';
import UserFidelityCard from './pages/account/fid/UserFidelityCard';

import ProductScreen from './pages/ProductScreen';
import OrderCheck from './pages/OrderCheck';
import OrderSuccess from './pages/OrderSuccess';
import EdenredScreen from './pages/EdenredScreen';

import PopupPayment from './payments/PopupPayment';
import GiftScreen from './pages/GiftScreen';
import InvitationScreen from './pages/Invitation';

import ResetPassword from './pages/auth/reset-password/ResetPassword';
import PromoScreen from './pages/promo/PromoScreen';
import PromoConversionScreen from './pages/promo/PromoConversionScreen';
import TicketScreen from './pages/account/TicketScreen';
import Recompenses from './pages/account/Recompense';
import Coupon from './pages/account/Coupon';
import Help from './pages/account/HelpScreen';
import RewardScreen from './pages/gift/RewardScreen';
import BrandDetailScreen from './pages/brand/BrandDetailScreen';
import BrandPartnerScreen from './pages/brand/BrandPartnerScreen';

import AddPaymentScreen from './pages/account/addPayment/AddPaymentScreen';
import AddCbWrapper from './pages/account/addPayment/AddCbWrapper';
import Wallet from './pages/account/addPayment/Wallet';
import LoginAddFidCardScreen from './pages/account/fid/add-card/LoginAddFidCardScreen';
import LoginAddFidScanScreen from './pages/account/fid/add-card/LoginAddFidScanScreen';
import FidLoginScreen from './pages/account/fid/FidLoginScreen';
import UpdateUserScreen from './pages/account/UpdateUserScreen';
import ChangeAmountJackpotScreen from './pages/jackpot/ChangeAmountJackpotScreen';

import PullToRefresh from 'pulltorefreshjs';
import ReactDOMServer from 'react-dom/server';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { isAppPWA } from './utils/helper-functions';
import { useSelector } from 'react-redux';
import { ProtectedRoute } from './middleware/ProtectedRoute';

import FidCouponScreen from './pages/FidCouponScreen';
import ReceiptScreen from './pages/ReceiptScreen';

import { GeolocationVerifModal } from './components/geolocation/modal/GeolocationVerifModal';
import StoreLocationScreen from './pages/StoreLocationScreen';

import { CrashReportProvider } from './context/CrashReportProvider';
import CrashReportModal from './components/crashReport/CrashReportModal';
import CrashReportEventListener from './components/crashReport/CrashReportEventListener';

import { NotificationProvider } from './context/notificationProvider';
import { AppNotification } from './components/crashReport/appNotification';

import GlobalNotificationPikkoToast from './components/toasts/GlobalNotificationPikkoToast';
import useAxiosInterceptors from './axios/useAxiosInterceptors';
import { PerformanceContextProvider } from './context/PerformanceContext';
import { NavigationTracker } from './components/NavigationTracker';

import { ToastProvider } from './components/toasts/provider/providerPikkoToast';
import PrivacyPolicy from './pages/media/policy';
import { PhoneLoginRooter } from './pages/phoneLogin/PhoneLoginRooter';

function App() {
  useAxiosInterceptors();
  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isAndroid = /android/i.test(userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);

    const manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';

    // Load the correct manifest based on the detected OS
    if (isAndroid) {
      manifestLink.href = '/manifest-android.json';
    } else if (isIOS) {
      manifestLink.href = '/manifest-ios.json';
    } else {
      manifestLink.href = '/manifest-default.json';
    }
    document.head.appendChild(manifestLink);

    return () => {
      document.head.removeChild(manifestLink);
    };
  }, []);

  useEffect(() => {
    if (document.querySelector('#pullToRefreshArea')) {
      PullToRefresh.init({
                           mainElement: '#pullToRefreshArea',
                           triggerElement: '#pullToRefreshArea',
                           shouldPullToRefresh() {
                             return isAppPWA();
                           },
                           onRefresh() {
                             window.location.reload();
                           },
                           iconArrow: ReactDOMServer.renderToString(
                             <FontAwesomeIcon icon={faArrowDown} size={'2x'} />,
                           ),
                           iconRefreshing: ReactDOMServer.renderToString(
                             <FontAwesomeIcon icon={faSyncAlt} spin={true} size={'2x'} />,
                           ),
                           instructionsPullToRefresh: ' ',
                           instructionsReleaseToRefresh: ' ',
                           instructionsRefreshing: ' ',
                         });
    }
  });

  window.scrollTo(0, 0);

  const actualTheme = useSelector((state) => state.theme.theme);

  // Sentry user feedBack config
  const configSelectorSentry = {
    shadowRootId: 'sentry-feedback',
    selector: '.widget__actor',
    maxRetries: 20,
    retryInterval: 150,
  };

  const routeStylesForSentry = {
    '/': {
      bottom: '70%',
      rigth: '0%',
    },
    '/home': {
      bottom: '80%',
      rigth: '0%',
    },
    '/scan': {
      bottom: '83%',
      rigth: '0%',
    },
    '/cart': {
      bottom: '89%',
      rigth: '0%',
    },
    '/receipt': {
      bottom: '53%',
      rigth: '0%',
    },
    '/account': {
      bottom: '85%',
      rigth: '0%',
    },
  };

  useShadowDOMStyling(configSelectorSentry, routeStylesForSentry);

  // Sentry userFeedBack Drawer
  const { isDrawerOpen, closeDrawer, sentryButtonRef } = useFeedbackDrawer();

  return (
    <>
      <ThemeProvider theme={actualTheme}>
        <ToastProvider>
        <PerformanceContextProvider>
        <CrashReportProvider>
          <CrashReportEventListener />
          <CrashReportModal />
          <NavigationTracker />
          <CriticalErrorBoundary>
            <NotificationProvider>

              <UserFeedbackDrawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                triggerRef={sentryButtonRef}
              />

              <AppNotification />
              <GlobalNotificationPikkoToast />

              <div id="pullToRefreshArea">

                <CacheBuster
                  currentVersion={process.env.REACT_APP_VERSION}
                  isEnabled={true}
                >
                  {/* <BrowserRouter> */}
                  <div className="home">
                    {/*<GeolocationVerifModal />*/}
                    <main>
                      {/*<PullToRefreshComponent />*/}
                      <React.Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route path="/" element={<RedirectPage />} />
                          <Route path={POLICY} element={<PrivacyPolicy />} />
                          <Route path={STORE_LIST} element={<StoreListScreen />} />
                          <Route path={STORE_LOCATION} element={<StoreLocationScreen />} />
                          <Route path={HOME} element={<WelcomeScreen />} />

                          <Route path={CONFIG} element={<ConfigScreen />} />

                          <Route
                            path={INVITATION}
                            element={<InvitationScreen />}
                          />
                          <Route path={PRODUCT} element={<ProductScreen />} />
                          <Route path={EDENRED} element={<EdenredScreen />} />
                          <Route
                            path={EDENRED_CONFIG}
                            element={<EdenredScreen />}
                          />
                          <Route path={CHECK} element={<OrderCheck />} />
                          <Route
                            path={ORDER_SUCCESS}
                            element={<OrderSuccess />}
                          />
                          <Route
                            path={SCAN}
                            element={
                              <ProtectedRoute>
                                <Scan />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path={CART}
                            element={
                              <ProtectedRoute>
                                <CartScreen />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path={SCAN_CHECK}
                            element={
                              <ProtectedRoute>
                                <ScanCheck />
                              </ProtectedRoute>
                            }
                          />
                          <Route path={RECEIPT} element={<ReceiptScreen />} />
                          <Route path={LOGIN} element={<LoginScreen />} />
                          <Route path={REGISTER} element={<RegisterScreen />} />
                          <Route path={ACCOUNT} element={<AccountScreen />} />
                          <Route
                            path={SET_ACCOUNT}
                            element={<SetAccountScreen />}
                          />
                          <Route
                            path={RESETPASSWORD}
                            element={<ResetPassword />}
                          />
                          <Route path={TICKETS} element={<TicketScreen />} />
                          <Route path={COUPONS} element={<Coupon />} />
                          <Route path={RECOMPENSES} element={<Recompenses />} />
                          <Route path={HELP} element={<Help />} />
                          <Route path={POPUP_PAYMENT} element={<PopupPayment />} />
                          <Route path={GIFT} element={<GiftScreen />} />
                          <Route path={REWARD} element={<RewardScreen />} />
                          <Route
                            path={BRAND_DETAIL}
                            element={<BrandDetailScreen />}
                          />
                          <Route path={BRAND} element={<BrandPartnerScreen />} />
                          <Route
                            path={PROMO}
                            element={
                              <ProtectedRoute>
                                <PromoScreen />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path={FIDCOUPON}
                            element={
                              <ProtectedRoute>
                                <FidCouponScreen />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path={CONVERSION}
                            element={<PromoConversionScreen />}
                          />
                          <Route path={WALLET} element={<Wallet />} />
                          <Route path={ADD_PAYMENT} element={<AddPaymentScreen />} />
                          <Route path={ADD_CB} element={<AddCbWrapper />} />
                          <Route
                            path={LOGIN_FID_CARD}
                            element={<LoginAddFidCardScreen />}
                          />
                          <Route
                            path={LOGIN_FID_SCAN}
                            element={<LoginAddFidScanScreen />}
                          />
                          <Route
                            path={ADD_FID_CARD}
                            element={<UserAddFidCardScreen />}
                          />
                          <Route
                            path={ADD_FID_SCAN}
                            element={<UserAddFidScanScreen />}
                          />
                          <Route path={FIDLOGIN} element={<FidLoginScreen />} />
                          <Route
                            path={USER_FID_CARD}
                            element={<UserFidelityCard />}
                          />
                          <Route
                            path={USER_FID_CARD_DETAIL}
                            element={<UserFidelityCardDetail />}
                          />
                          <Route
                            path={NUMBER_SCREEN}
                            element={<NumberScreen allowOtherLoginMethod={true} />}
                          />
                          <Route path={`${PHONE_PASSWORD_LOGIN}/*`} element={<PhoneLoginRooter />} />
                          <Route path={UPDATE_USER} element={<UpdateUserScreen />} />
                          <Route path={AMOUNT_CHANGE_JACKPOT} element={<ChangeAmountJackpotScreen />} />
                          {/* <Route path="/Recompense" element = {<Recompense/>} /> */}
                        </Routes>
                      </React.Suspense>
                    </main>
                  </div>
                  {/* </BrowserRouter> */}
                </CacheBuster>
              </div>
            </NotificationProvider>
          </CriticalErrorBoundary>
        </CrashReportProvider>
        </PerformanceContextProvider>
        </ToastProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
